import React, { useEffect, useMemo, useState } from 'react';
import { RadioCardItem, RadioCardRoot } from '@/components/ui/radio-card';
import { Box, HStack, Stack, Text } from '@chakra-ui/react';
import { CheckboxIcon } from '@/_assets/icon-components';
import { isWorkingHour } from '@/_utils/isWorkingHour';
import { IRedux } from '@/_store/types';
import { useSelector } from 'react-redux';
import { IWorkHour } from '@/_types';
import { useTranslations } from 'next-intl';
import dayjs, { Dayjs } from 'dayjs';
import { useQuery } from '@tanstack/react-query';
import getShipperById from '@/_service/shipper.service';
import { useActiveBrand } from '../../_hooks/useActiveBrand';

const format = 'HH:mm';

export default function TimeSelector({
  value,
  isToday,
  selectedDay,
  workHours,
  interval,
  fromTime,
  onChange,
}: {
  value: string;
  interval: number;
  isToday: boolean;
  selectedDay: dayjs.Dayjs;
  workHours: IWorkHour | null;
  /** When set, only show time slots at or after this time (same day). Used e.g. for "end time" after "start time". */
  fromTime?: Dayjs | null;
  onChange: (e: string) => void;
}) {
  const [tillTmrw, setTillTmrw] = useState<string[]>([]); // Time until tomorrow

  const common = useSelector((state: IRedux) => state.common);
  const settings = useSelector((state: IRedux) => state.settings);
  const { order_type } = useSelector((state: IRedux) => state.session);

  const t = useTranslations();
  const shipperId = useActiveBrand()
  const storeBranch = common?.branches?.[shipperId]

  const { data: shipper } = useQuery({
    queryKey: ['get-shipper-by-id', settings?.data?.shipper_id],
    queryFn: () =>
      getShipperById(settings?.data?.shipper_id || '').then((res) => res.data),
    enabled: Boolean(settings?.data?.shipper_id),
  });

  const schedule = useMemo(
    () => ({
      start:
        workHours?.from_time ||
        storeBranch?.work_hour_start ||
        shipper?.work_hour_start ||
        '00:00',
      end:
        workHours?.to_time ||
        storeBranch?.work_hour_end ||
        shipper?.work_hour_end ||
        '23:59',
    }),
    [workHours, storeBranch, shipper]
  );

  useEffect(() => {
    const generateTimeSlots = () => {
      if (!schedule.start || !schedule.end || !selectedDay) return [];

      const workStart = dayjs(schedule.start, format, true);
      const workEnd = dayjs(schedule.end, format, true);

      if (!workStart.isValid() || !workEnd.isValid()) return [];

      const slots: string[] = [];
      const slotIncrement = 30;

      let currentSlot = getStartSlot(workStart);

      // When fromTime is set (e.g. end time after start time), start at first slot strictly after fromTime
      if (fromTime && selectedDay.isSame(fromTime, 'day')) {
        const firstSlotAfterFrom =
          fromTime.minute() === 0
            ? fromTime.clone().minute(30)
            : fromTime.minute() < 30
              ? fromTime.clone().startOf('hour').minute(30)
              : fromTime.clone().add(1, 'hour').startOf('hour');
        if (currentSlot.isSameOrBefore(fromTime, 'minute')) {
          currentSlot = firstSlotAfterFrom;
        }
      }

      const endBoundary = getEndBoundary(workStart, workEnd);
      let remaining =
        currentSlot.minute() > 30
          ? 60 - currentSlot.minute()
          : 30 - currentSlot.minute();

      while (currentSlot.isSameOrBefore(endBoundary)) {
        slots.push(currentSlot.format('HH:mm'));

        if (remaining > 0) {
          currentSlot = currentSlot.add(remaining, 'minute');
          remaining = 0;
        } else {
          currentSlot = currentSlot.add(slotIncrement, 'minute');
        }
      }

      return slots;

      function getStartSlot(workStart: Dayjs) {
        if (!isToday) {
          return selectedDay.clone().hour(workStart.hour()).minute(interval);
        }

        const start = workStart.isBefore(dayjs()) ? dayjs() : workStart;
        const result = start.add(interval, 'minute');

        return result.minute() > 30
          ? result.startOf('hour')
          : result.minute(30);
      }

      function getEndBoundary(workStart: Dayjs, workEnd: Dayjs) {
        return workStart.isBefore(workEnd)
          ? selectedDay.clone().hour(workEnd.hour()).minute(workEnd.minute())
          : selectedDay.clone().endOf('day');
      }
    };
    setTillTmrw(generateTimeSlots());
  }, [interval, selectedDay, schedule, fromTime]);

  return (
    <RadioCardRoot
      py={4}
      unstyled
      value={value}
      onValueChange={(e: any) => {
        onChange(e.value);
      }}
    >
      <Stack
        display='grid'
        gridTemplateColumns={{ base: '1fr', lg: '1fr 1fr' }}
        gap={{ base: 0, lg: 4 }}
      >
        {isToday &&
          isWorkingHour(schedule.start, schedule.end) &&
          interval > 0 && (
            <HStack
              w='full'
              as='label'
              py={3}
              ml={{ base: -2, lg: 0 }}
              gap={{ base: 2, lg: 0 }}
              fontWeight={{ base: 400, lg: 500 }}
              fontSize={{ base: 'sm', lg: 'xl' }}
              rounded={{ base: 'none', lg: '2xl' }}
              h={{ base: 'fit-content', lg: '56px' }}
              borderWidth={{ base: '0px 0px 1px 0px', lg: '1px' }}
              borderStyle='solid'
              borderColor={{
                base: 'background',
                lg: value === '' ? 'primary' : 'background',
              }}
              color={'text'}
            >
              <RadioCardItem
                px={0}
                icon={
                  <Box display={{ base: 'block', lg: 'none' }}>
                    {value === '' ? (
                      <CheckboxIcon color={'var(--primary-default)'} />
                    ) : (
                      <Box bg='light.300' w={7} h={7} rounded='full' />
                    )}
                  </Box>
                }
                value={''}
                id={'today'}
                indicator={false}
                border='none'
              />
              <Text px={4}>
                {t('soonest')}
                <Text as='span' display={{ base: 'inline', lg: 'none' }}>
                  ,{' '}
                  {t('in_x_min', {
                    x:
                      (order_type?.type === 'delivery' &&
                        storeBranch?.delivery_time) ||
                      interval ||
                      60,
                  })}
                </Text>
                <Text as='span' display={{ base: 'none', lg: 'inline' }}>
                  {' '}
                  (
                  {(order_type?.type === 'delivery' &&
                    storeBranch?.delivery_time) ||
                    interval ||
                    60}{' '}
                  {t('time')})
                </Text>
              </Text>
            </HStack>
          )}
        {tillTmrw?.map(
          (item, idx) =>
            tillTmrw.length - 1 !== idx && (
              <HStack
                py={3}
                ml={{ base: -2, lg: 0 }}
                w='full'
                as='label'
                key={item}
                gap={{ base: 2, lg: 0 }}
                fontWeight={{ base: 400, lg: 500 }}
                fontSize={{ base: 'sm', lg: 'xl' }}
                rounded={{ base: 'none', lg: '2xl' }}
                h={{ base: 'fit-content', lg: '56px' }}
                borderWidth={{ base: '0px 0px 1px 0px', lg: '1px' }}
                borderStyle='solid'
                color={'text'}
                borderColor={{
                  base: 'background',
                  lg:
                    value ===
                    selectedDay.format('YYYY-MM-DD') + ' ' + item + ':00'
                      ? 'primary'
                      : 'background',
                }}
              >
                <RadioCardItem
                  px={0}
                  pl={4}
                  icon={
                    <Box display={{ base: 'block', lg: 'none' }}>
                      {value ===
                      selectedDay.format('YYYY-MM-DD') + ' ' + item + ':00' ? (
                        <CheckboxIcon color={'var(--primary-default)'} />
                      ) : (
                        <Box bg='background' w={7} h={7} rounded='full' />
                      )}
                    </Box>
                  }
                  value={selectedDay.format('YYYY-MM-DD') + ' ' + item + ':00'}
                  id={'today'}
                  indicator={false}
                  border='none'
                />
                <p>{item}</p>
              </HStack>
            )
        )}
      </Stack>
    </RadioCardRoot>
  );
}
